'use client';
import { useAuthMutations, useFirebaseAuthMutations } from './mutations';

import { APP_ROUTES } from '@/routes';
import type { LoginFormType } from '../schemas';
import { useAuthStore } from '../stores/useAuthStore';
import { useRouter } from 'next/navigation';
import { useToast } from '@packages/chakra-v3';
import { CART_QUERY_KEYS } from '@/modules/cart';
import { tsrClient } from '@/libs/ts-rest/ts-client';

const useLogin = () => {
	const { showError, showSuccess } = useToast();
	const { firebaseLoginWithCredential, firebaseLoginWithProvider } = useFirebaseAuthMutations();
	const { loginMutation, loginWithProviderMutation } = useAuthMutations();
	const { setAuthStore } = useAuthStore();
	const queryClient = tsrClient.useQueryClient();
	const invalidateGuestCartQuery = () => queryClient.invalidateQueries({ queryKey: CART_QUERY_KEYS.GUEST() });

	const router = useRouter();
	const handleSuccessLogin = () => {
		invalidateGuestCartQuery();
		showSuccess({
			title:       'Login successful',
			description: 'You have been logged in',
		});
	};

	const handleLoginError = () => {
		showError({
			title:       'Login failed',
			description: 'Please check your email and password and try again',
		});
	};

	const loginWithToken = async(token: string) => {
		return await loginMutation.mutateAsync(
			{ headers: { authorization: `Bearer ${token}` } },
			{
				onSuccess: handleSuccessLogin,
				onError:   handleLoginError,
			},
		);
	};

	const handleLoginWithCredential = async(loginFormData: LoginFormType) => {
		await firebaseLoginWithCredential.mutateAsync(loginFormData, {
			onSuccess: async({ idToken }) => {
				const result = await loginWithToken(idToken);
				setAuthStore({
					user:   result.body.data.session,
					status: 'authenticated',
				});
				router.push(APP_ROUTES.HOME);
			},
			onError: handleLoginError,
		});
	};

	const handleLoginWithProvider = async() => {
		await firebaseLoginWithProvider.mutateAsync(true, {
			onSuccess: async({ idToken, user }) => {
				await loginWithProviderMutation.mutateAsync(
					{ headers: { authorization: `Bearer ${idToken}` } },
					{
						onSuccess: async(response) => {
							if (response.status === 201) {
								const newToken = await user.getIdToken(true);
								const result = await loginWithToken(newToken);
								setAuthStore({
									user:   result.body.data.session,
									status: 'authenticated',
								});
								router.push(APP_ROUTES.HOME);

								return;
							}

							setAuthStore({
								user:   response.body.data.session,
								status: 'authenticated',
							});
							handleSuccessLogin();
							router.push(APP_ROUTES.HOME);
						},
						onError: handleLoginError,
					},
				);
			},
			onError: handleLoginError,
		});
	};

	return {
		handleLoginWithCredential,
		handleLoginWithProvider,
	};
};

export default useLogin;
